/**
 * apps/battle/client/src/terminal/narrative.ts
 * Phase 4.0 Terminal Mode — hardcoded default narrative copy.
 *
 * Lives here (not in @econquest/shared) per spec §15 to keep this string
 * bundle out of other apps' builds. SME-editable in 4.1 via DB migration.
 *
 * Voice: terse, second-person, faintly heroic, never cute.
 * No exclamations, no emoji. Max 4 sentences (entry) / 2 sentences (exit).
 */

export type ClusterId =
  | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I'
  | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q';

export type PortalKey =
  // Permanent (12-mark, pass-bar ≥ 8/12)
  | 'A-N' | 'H-K' | 'I-J' | 'I-K'
  // Conditional (9-mark, pass-bar ≥ 5/9)
  | 'B-L' | 'B-J' | 'C-Q' | 'C-O' | 'E-N' | 'F-N' | 'G-P' | 'H-P' | 'M-Q';

export interface ClusterNarrative {
  name: string;          // verbatim from dag.json
  entry: string;         // 2–4 sentences, on first entry into the cluster
  exit: string;          // 1–2 sentences, on first clear
}

export interface PortalNarrative {
  name: string;          // short theme, drawn from portal description
  entry: string;
  exit: string;
}

// ---------------------------------------------------------------------------
// Cluster narrative — 17 entries (A..Q)
// ---------------------------------------------------------------------------

export const CLUSTER_NARRATIVE: Record<ClusterId, ClusterNarrative> = {
  A: {
    name: 'Economic Problem & Opportunity Cost',
    entry:
      'You step into the village of Scarcity. The elders show you empty hands, full hearts, and one road that always forks. ' +
      'Every choice you make from here will demand its price. Learn it well — the cost of one path is the loss of another.',
    exit:
      'The villagers bow. The cost of choice is yours to carry; the road north opens.',
  },
  B: {
    name: 'Demand & Supply',
    entry:
      'You enter the marketplace. Buyers crowd one side of the square; sellers line the other. ' +
      'Prices rise and fall like tides between them. Read the currents, or be carried by them.',
    exit:
      'The market quiets. You have learned how its prices breathe.',
  },
  C: {
    name: 'Elasticity (PED / PES / YED / XED)',
    entry:
      'The market stretches and snaps under your hand. Some goods bend a great deal at the smallest nudge in price; others barely move at all. ' +
      'Measure how much they bend, and you will know what taxes raise, what subsidies fail, and what cartels can hold.',
    exit:
      'You have measured the give in every market. Numbers obey you now.',
  },
  D: {
    name: 'Market Failure',
    entry:
      'You walk a coast strewn with overfished nets and choked with smoke from a free chimney. The market made these choices; nobody chose them. ' +
      'When the price tells the wrong story, the wrong story gets built. Name each failure before you try to fix it.',
    exit:
      'You have named the failures. The price is no longer the only story.',
  },
  E: {
    name: 'Externalities',
    entry:
      'A factory pours profit into the town and soot into its lungs. A vaccine costs one but spares thousands. ' +
      'Costs and benefits spill onto strangers. Trace where they land before you tax, subsidise, or regulate.',
    exit:
      'You have followed the spill to the ones who bear it. The ledger is more honest now.',
  },
  F: {
    name: 'Government Intervention (Micro)',
    entry:
      'The state lays its hand on the market: a tax, a subsidy, a ceiling, a floor. Each tool answers a failure; each tool creates one. ' +
      'Judge the trade-off before you reach for it.',
    exit:
      'You have weighed each tool by its cost. The state is yours to use, not to wield.',
  },
  G: {
    name: 'Business Costs & Economies of Scale',
    entry:
      'You step into the factory floor. Fixed costs loom overhead; variable costs trickle through every shift. ' +
      'Scale rewards the patient and crushes the small. Learn the curves before you sign the lease.',
    exit:
      'You have read the cost curves. You know when bigger pays — and when it breaks.',
  },
  H: {
    name: 'Market Structures',
    entry:
      'Four kingdoms govern the marketplace: perfect competition, monopoly, oligopoly, monopolistic. Each sets prices by its own law. ' +
      'Know which kingdom you stand in before you make your move.',
    exit:
      'You have mapped the four kingdoms. No market will catch you guessing again.',
  },
  I: {
    name: 'Labour Market',
    entry:
      'You enter the hiring hall. Wages are prices for time; demand for labour follows demand for what labour makes. ' +
      'Unions, minimum wages, migration, training — each bends the curve. Watch where the curve bends back.',
    exit:
      'You have seen wages move. The labour market keeps no secrets from you now.',
  },

  // ----- World 2 ----------------------------------------------------------

  J: {
    name: 'National Income & GDP',
    entry:
      'You step into the macroeconomic realm. Here a nation\u2019s income is measured in totals — output, expenditure, income — three lenses on one figure. ' +
      'Learn what GDP counts, and what it never sees.',
    exit:
      'You have counted what a country earns. The macroeconomic realm opens before you.',
  },
  K: {
    name: 'Economic Growth',
    entry:
      'A nation grows when its output rises. The roads lengthen, the factories hum, and the children read further than their parents. ' +
      'Growth is a long road; learn its drivers, and learn its costs.',
    exit:
      'You have seen the engines of growth. The road is yours to lay.',
  },
  L: {
    name: 'Inflation',
    entry:
      'Prices climb like a slow tide. A loaf that cost one coin last year now costs two; wages are slower to follow. ' +
      'Find the causes — demand-pull, cost-push, expectations — before you choose a remedy.',
    exit:
      'You have named what makes prices rise. The tide no longer rises unseen.',
  },
  M: {
    name: 'Monetary Policy',
    entry:
      'You enter the central bank. A single dial — the interest rate — moves borrowing, spending, the currency, the housing market, every market downstream. ' +
      'Turn it slowly. Turn it late, and the country pays.',
    exit:
      'You have felt the weight of the dial. Markets answer when you turn it now.',
  },
  N: {
    name: 'Fiscal Policy',
    entry:
      'The treasury opens its ledger to you. Taxes on one side, spending on the other; the gap is the deficit, the sum is the debt. ' +
      'Every coin you raise must be paid by someone, somewhere, in some year.',
    exit:
      'You have balanced the ledger. The treasury answers to your hand.',
  },
  O: {
    name: 'Balance of Payments',
    entry:
      'You stand at the harbour and watch the boats. Exports leave; imports return; capital flows beneath the waterline. ' +
      'Every account must balance against another. Learn where the deficit truly lives.',
    exit:
      'You have read the harbour ledger. Surplus and deficit are no longer mysteries.',
  },
  P: {
    name: 'International Trade',
    entry:
      'The borders open. Comparative advantage promises gains; protectionism promises safety; both promise something true and something false. ' +
      'Weigh the tariff, the quota, the subsidy, the deal — by who gains and who loses, not by whose flag flies highest.',
    exit:
      'You have weighed the gains from trade against the gains from protection. Neither flag blinds you now.',
  },
  Q: {
    name: 'Exchange Rates',
    entry:
      'You enter the currency markets. A pound buys more dollars today than yesterday; an exporter cheers, an importer groans. ' +
      'Find what moves the rate, and you will know what moves the rest.',
    exit:
      'You have followed the currency. The rest of the macro story follows it too.',
  },
};

// ---------------------------------------------------------------------------
// Portal narrative — 13 entries (4 permanent + 9 conditional)
// ---------------------------------------------------------------------------

export const PORTAL_NARRATIVE: Record<PortalKey, PortalNarrative> = {
  // Permanent
  'A-N': {
    name: 'Opportunity cost of policy',
    entry:
      'A bridge of pale light shimmers between Cluster A and Cluster N. Opportunity cost is the toll on every fiscal road. ' +
      'Cross now and you will carry that knowledge into every policy you ever judge.',
    exit:
      'The bridge holds. You may cross again whenever a policy demands its cost.',
  },
  'H-K': {
    name: 'Privatisation and growth',
    entry:
      'A second bridge opens between market structures and the engines of growth. Competition policy and privatisation are levers on a nation\u2019s supply side. ' +
      'Cross, and your growth essays will gain a sharper edge.',
    exit:
      'The bridge is set. Supply-side policy now stands among your tools.',
  },
  'I-J': {
    name: 'Labour and national income',
    entry:
      'A bridge spans the labour market and the macroeconomic accounts. What households earn, the nation earns; what the nation spends, households receive back. ' +
      'Cross, and the circular flow becomes more than a diagram.',
    exit:
      'The circular flow is whole. Labour and income now answer to each other.',
  },
  'I-K': {
    name: 'Human capital and growth',
    entry:
      'A bridge of slow, steady light runs from the labour hall to the engines of growth. Schools, training, healthcare — each adds to human capital. ' +
      'Cross, and you will see why some countries pull ahead and stay there.',
    exit:
      'You have crossed. Human capital is now a permanent line in your growth argument.',
  },

  // Conditional
  'B-L': {
    name: 'AD/AS and demand-pull inflation',
    entry:
      'A faint arc connects the marketplace to the inflation chapter. AD and AS are demand and supply, drawn at national scale. ' +
      'Cross, and your inflation answers will hold their shape.',
    exit:
      'The arc holds. Macro now reads as micro\u2019s larger twin.',
  },
  'B-J': {
    name: 'Demand-and-supply and the multiplier',
    entry:
      'A second arc lights up between the marketplace and national income. The multiplier is just demand and supply, played out across an economy. ' +
      'Cross, and the multiplier will stop feeling like a separate trick.',
    exit:
      'The arc settles. The multiplier is no longer a stranger.',
  },
  'C-Q': {
    name: 'Trade elasticity and exchange rates',
    entry:
      'An arc bends from the elasticity chapter to the currency markets. Whether a depreciation helps or hurts an exporter depends on PED. ' +
      'Cross, and exchange-rate essays gain real numbers.',
    exit:
      'The arc steadies. You will never argue depreciation without elasticity again.',
  },
  'C-O': {
    name: 'Elasticity and the trade balance',
    entry:
      'An arc opens between elasticity and the balance of payments. The Marshall–Lerner condition is just two elasticities, added. ' +
      'Cross, and the current account stops surprising you.',
    exit:
      'You have crossed. Marshall–Lerner is now a tool, not a phrase.',
  },
  'E-N': {
    name: 'Externalities meet fiscal policy',
    entry:
      'An arc lights up between externalities and the treasury. A carbon tax corrects a market failure and raises revenue in the same stroke. ' +
      'Cross, and your policy essays will speak two languages at once.',
    exit:
      'The arc holds. One tax, two purposes — you can argue both.',
  },
  'F-N': {
    name: 'Micro intervention to macro fiscal',
    entry:
      'An arc reaches from microeconomic intervention to the national ledger. Every subsidy and every tax flows back into the fiscal budget. ' +
      'Cross, and the boundary between micro and macro fiscal thinking blurs as it should.',
    exit:
      'The arc settles. Micro and macro fiscal policy now read as one ledger.',
  },
  'G-P': {
    name: 'Scale and comparative advantage',
    entry:
      'An arc opens from the factory floor to the trading harbour. Economies of scale lower unit costs, and lower unit costs are the root of comparative advantage. ' +
      'Cross, and trade theory grows costs of its own.',
    exit:
      'You have crossed. Comparative advantage now stands on a cost curve.',
  },
  'H-P': {
    name: 'Market structures and protectionism',
    entry:
      'An arc connects the four market kingdoms to the trading borders. Infant industry and domestic monopoly are the oldest arguments against free trade. ' +
      'Cross, and the case for protection becomes a structural one.',
    exit:
      'The arc holds. You can now argue protection without sounding defensive.',
  },
  'M-Q': {
    name: 'Monetary policy and exchange rates',
    entry:
      'An arc runs from the central bank to the currency markets. A rate rise pulls capital in, lifts the currency, and cools import prices. ' +
      'Cross, and the transmission mechanism stops skipping a step.',
    exit:
      'The arc settles. Interest rates and exchange rates now move together in your mind.',
  },
};
